import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { toast } from 'react-toastify';
import { axiosNoAuth } from '../config/axiosInstance';
import { NINJACURATE_BACKEND_BASE_URL } from '../config/api_config';

// Async thunk for saving user info for session
export const saveUserInfo = createAsyncThunk(
    'userInfo/saveUserInfo',
    async ({ session_id, name, email, phone }, { rejectWithValue }) => {
        try {
            const response = await axiosNoAuth.post(`${NINJACURATE_BACKEND_BASE_URL}/personalise/session/user-details`, {
                session_id,
                name,
                email,
                phone
            }, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });
           
            return response.data;
        } catch (error) {
            console.error('Error saving user info:', error);
            const message = error?.response?.data || error?.message || 'Failed to save user details';
            toast.error('Failed to save user details');
            return rejectWithValue(message);
        }
    }
);

// Async thunk for fetching personalized syllabus
export const fetchPersonalizedSyllabus = createAsyncThunk(
    'userInfo/fetchPersonalizedSyllabus',
    async ({ session_id }, { rejectWithValue }) => {
        const maxAttempts = 30; // Maximum number of polling attempts (5 minutes at 10s intervals)
        const pollingInterval = 10000; // 10 seconds between attempts
        
        const pollAPI = async (attempt = 1) => {
            try {
                const response = await axiosNoAuth.get(`${NINJACURATE_BACKEND_BASE_URL}/personalise/session/personalised-syllabus`, {
                    params: {
                        session_id
                    },
                    headers: {
                        'accept': 'application/json'
                    }
                });
                
                // If we get a 200 response, return the data
                if (response.status === 200) {
                    return response.data;
                }
                
                // If not 200 but no error thrown, continue polling
                throw new Error(`Received status ${response.status}, continuing to poll...`);
                
            } catch (error) {
                console.log(`Polling attempt ${attempt}/${maxAttempts} failed:`, error.message);
                
                // If we've reached max attempts, reject with error
                if (attempt >= maxAttempts) {
                    console.error('Max polling attempts reached for personalized syllabus');
                    toast.error('Failed to fetch syllabus after multiple attempts');
                    const message = error?.response?.data || error?.message || 'Failed to fetch personalized syllabus after polling';
                    throw new Error(message);
                }
                
                // Wait for the polling interval before next attempt
                await new Promise(resolve => setTimeout(resolve, pollingInterval));
                
                // Recursively call with incremented attempt count
                return pollAPI(attempt + 1);
            }
        };
        
        try {
            return await pollAPI();
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);


export const fetchPersonalizedSyllabusForBeginner = createAsyncThunk(
    'userInfo/fetchPersonalizedSyllabusForBeginner',
    async ({ skill_name, goal }, { rejectWithValue }) => {
        const maxAttempts = 30; // Maximum number of polling attempts (5 minutes at 10s intervals)
        const pollingInterval = 10000; // 10 seconds between attempts
        
        const pollAPI = async (attempt = 1) => {
            try {
                const response = await axiosNoAuth.get(`${NINJACURATE_BACKEND_BASE_URL}/get_syllabus`, {
                    params: {
                        skill_name,
                        goal
                    },
                    headers: {
                        'accept': 'application/json'
                    }
                });
                
                // If we get a 200 response, return the data
                if (response.status === 200) {
                    return response.data;
                }
                
                // If not 200 but no error thrown, continue polling
                throw new Error(`Received status ${response.status}, continuing to poll...`);
                
            } catch (error) {
                console.log(`Polling attempt ${attempt}/${maxAttempts} failed:`, error.message);
                
                // If we've reached max attempts, reject with error
                if (attempt >= maxAttempts) {
                    console.error('Max polling attempts reached for personalized syllabus for beginner');
                    toast.error('Failed to fetch syllabus for beginner after multiple attempts');
                    const message = error?.response?.data || error?.message || 'Failed to fetch personalized syllabus for beginner after polling';
                    throw new Error(message);
                }
                
                // Wait for the polling interval before next attempt
                await new Promise(resolve => setTimeout(resolve, pollingInterval));
                
                // Recursively call with incremented attempt count
                return pollAPI(attempt + 1);
            }
        };
        
        try {
            return await pollAPI();
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);


export const fetchPersonalizedRoles = createAsyncThunk(
    'userInfo/fetchPersonalizedRoles',
    async ({ session_id }) => {
        try {
            const response = await axiosNoAuth.get(`${NINJACURATE_BACKEND_BASE_URL}/personalise/session/roles`, {
                params: {
                    session_id
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching personalized roles:', error);
            //toast.error('Failed to fetch personalized syllabus for beginner');
        }
    }
);

const initialState = {
    userInfo: {
        session_id: '',
        name: '',
        email: '',
        phone: '',
        uuid: ''
    },
    personalizedSyllabus: [],
    personalizedProjects : [],
    personalizedOutcomes : [],
    personalizedRoles : [],
    loading: false,
    error: null,
    isSaving: false,
    isFetchingSyllabus: false,
    syllabusError: null,
    isFetchingRoles: false,
    rolesError: null
};

// Redux slice for user info
const userInfoSlice = createSlice({
    name: 'userInfo',
    initialState,
    reducers: {
        setUserInfo: (state, action) => {
            state.userInfo = { ...state.userInfo, ...action.payload };
        },
        updateUserField: (state, action) => {
            const { field, value } = action.payload;
            state.userInfo[field] = value;
        },
        clearUserInfo: (state) => {
            state.userInfo = initialState.userInfo;
            state.error = null;
        },
        setUuid: (state, action) => {
            state.userInfo.uuid = action.payload;
        },
        setSessionId: (state, action) => {
            state.userInfo.session_id = action.payload;
        },
        clearError: (state) => {
            state.error = null;
        },
        clearPersonalizedSyllabus: (state) => {
            state.personalizedSyllabus = null;
            state.syllabusError = null;
        },
        clearSyllabusError: (state) => {
            state.syllabusError = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // Save user info cases
            .addCase(saveUserInfo.pending, (state) => {
                state.isSaving = true;
                state.error = null;
            })
            .addCase(saveUserInfo.fulfilled, (state, action) => {
                state.isSaving = false;
                state.userInfo = { ...state.userInfo, ...action.payload };
                state.error = null;
            })
            .addCase(saveUserInfo.rejected, (state, action) => {
                state.isSaving = false;
                state.error = action.payload;
            })
            // Fetch personalized syllabus cases
            .addCase(fetchPersonalizedSyllabus.pending, (state) => {
                state.isFetchingSyllabus = true;
                state.syllabusError = null;
            })
            .addCase(fetchPersonalizedSyllabus.fulfilled, (state, action) => {
                state.isFetchingSyllabus = false;
                state.personalizedSyllabus = action.payload?.syllabus;
                state.personalizedProjects = action.payload?.projects;
                state.personalizedOutcomes = action.payload?.outcomes;
                state.syllabusError = null;
            })
            .addCase(fetchPersonalizedSyllabus.rejected, (state, action) => {
                state.isFetchingSyllabus = false;
                state.syllabusError = action.payload;
            }).addCase(fetchPersonalizedSyllabusForBeginner.pending, (state) => {
                state.isFetchingSyllabus = true;
                state.syllabusError = null;
            }).addCase(fetchPersonalizedSyllabusForBeginner.fulfilled, (state, action) => {
                state.isFetchingSyllabus = false;
                state.personalizedSyllabus = {  ...action.payload?.syllabus, is_beginner: true };
                state.personalizedProjects = action.payload?.projects;
                state.personalizedOutcomes = action.payload?.outcomes;
                state.syllabusError = null;
            }).addCase(fetchPersonalizedSyllabusForBeginner.rejected, (state, action) => {
                state.isFetchingSyllabus = false;
                state.syllabusError = action.payload;
            }).addCase(fetchPersonalizedRoles.pending, (state) => {
                state.isFetchingRoles = true;
                state.rolesError = null;
            }).addCase(fetchPersonalizedRoles.fulfilled, (state, action) => {
                state.isFetchingRoles = false;
                state.personalizedRoles = action.payload?.roles;
                state.rolesError = null;
            }).addCase(fetchPersonalizedRoles.rejected, (state, action) => {
                state.isFetchingRoles = false;
                state.rolesError = action.payload;
            });
    },
});

export const fetchUUID = createAsyncThunk(
    'fetchUUID',
    async ({ name, email, phone_number, skill_name, medium, source }) => {
        try {
            const response = await axiosNoAuth.post(`${NINJACURATE_BACKEND_BASE_URL}/mobile_app/creation_id`, {
                name,
                email,
                phone_number,
                skill_name,
                medium,
                source
            })
            return response.data
        } catch (error) {
            console.log(error.message)
            throw error
        }
    }
)

export const fetchRazorpayId = createAsyncThunk(
    'fetchRazorpayId',
    async ({ uid, amount, product_name }) => {
        try {
            const response = await axiosNoAuth.post("https://xgfy-czuw-092q.m2.xano.io/api:IfQi_Nyx/razorpay_ninjacurate_payment/create_order", {
                uid,
                amount,
                product_name
            })
            return response.data
        } catch (error) {
            console.log(error.message)
            throw error
        }
    }
)
export const {
    setUserInfo,
    updateUserField,
    clearUserInfo,
    setUuid,
    setSessionId,
    clearError,
    clearPersonalizedSyllabus,
    clearSyllabusError
} = userInfoSlice.actions;

// Selectors
export const selectUserInfo = (state) => state.userInfo.userInfo;
export const selectUserInfoLoading = (state) => state.userInfo.loading;
export const selectUserInfoError = (state) => state.userInfo.error;
export const selectUserInfoSaving = (state) => state.userInfo.isSaving;
export const selectPersonalizedSyllabus = (state) => state.userInfo.personalizedSyllabus;
export const selectIsFetchingSyllabus = (state) => state.userInfo.isFetchingSyllabus;
export const selectPersonalizedProjects = (state) => state.userInfo.personalizedProjects;
export const selectPersonalizedOutcomes = (state) => state.userInfo.personalizedOutcomes;
export const selectPersonalizedRoles = (state) => state.userInfo.personalizedRoles;
export const selectSyllabusError = (state) => state.userInfo.syllabusError;

export default userInfoSlice.reducer;
